// Trade service - Firestore CRUD operations for trades
// Port of public/trade.js (framework-agnostic, no Svelte imports)

import { db } from './firebase.js';
import { doc, getDoc, setDoc, updateDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { generateRandomId } from '../logic/utils.js';
import { normalizeToShortUrl } from './recent-binders.js';
import { getUserToken } from '../stores/auth.svelte.js';

// Card status constants
export const CARD_STATUS = {
  TRADE: 'trade',
  CONSIDERING: 'considering',
  SUGGESTED: 'suggested',
};

// Reference to the trade store's pendingLocalChange flag.
// Set by setTradeFlags() during initialization from the store module.
let _tradeFlags = null;

/**
 * Connect the trade service to the trade store's reactive flags.
 * Called once from trade.svelte.js during initialization.
 * @param {{ setPendingLocalChange: (v: boolean) => void, setPendingExchangeUpdate: (v: boolean) => void }} flags
 */
export function setTradeFlags(flags) {
  _tradeFlags = flags;
}

function markPendingLocalChange() {
  if (_tradeFlags) _tradeFlags.setPendingLocalChange(true);
}

function markPendingExchangeUpdate() {
  if (_tradeFlags) _tradeFlags.setPendingExchangeUpdate(true);
}

// ============================================================
// Trade CRUD
// ============================================================

/**
 * Create a new trade
 * @param {string} userABinderUrl - URL of side A's binder
 * @param {string} userBBinderUrl - URL of side B's binder
 * @param {Array} initialCardsForB - Cards to pre-populate on side B
 * @param {Object} binderAInfo - { name, owner } for display
 * @param {Object} binderBInfo - { name, owner } for display
 * @returns {Promise<string>} The new trade ID
 */
export async function createTrade(
  userABinderUrl,
  userBBinderUrl,
  initialCardsForB = [],
  binderAInfo = {},
  binderBInfo = {}
) {
  const tradeId = generateRandomId(16);
  const userAToken = await getUserToken();

  // Normalize URLs to short form (e.g., /mfb.xxx) - see TRADE_DATA.md
  const normalizedUrlA = normalizeToShortUrl(userABinderUrl);
  const normalizedUrlB = normalizeToShortUrl(userBBinderUrl);

  const tradeData = {
    tradeId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    acceptedByA: false,
    acceptedByB: false,
    status: 'open',
    cancelledBy: null,
    cancelledAt: null,
    allowedTokens: [userAToken],
    sideA: {
      userToken: userAToken,
      binderUrl: normalizedUrlA,
      binderName: binderAInfo.name || null,
      ownerName: binderAInfo.owner || null,
      cards: [],
    },
    sideB: {
      userToken: null,
      binderUrl: normalizedUrlB,
      binderName: binderBInfo.name || null,
      ownerName: binderBInfo.owner || null,
      cards: initialCardsForB,
    },
  };

  await setDoc(doc(db, 'trades', tradeId), tradeData);
  return tradeId;
}

/**
 * Cancel a trade
 */
export async function cancelTrade(tradeId) {
  markPendingLocalChange();

  const userToken = await getUserToken();
  const tradeRef = doc(db, 'trades', tradeId);

  await updateDoc(tradeRef, {
    status: 'cancelled',
    cancelledBy: userToken,
    cancelledAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  console.log('[Trade] Trade cancelled:', tradeId);
}

/**
 * Load a trade by ID
 * @param {string} tradeId
 * @returns {Promise<import('../types.js').Trade>}
 */
export async function loadTrade(tradeId) {
  try {
    const tradeDoc = await getDoc(doc(db, 'trades', tradeId));
    if (!tradeDoc.exists()) {
      console.error('[Trade] Trade document does not exist:', tradeId);
      throw new Error('Trade not found');
    }
    return /** @type {import('../types.js').Trade} */ ({ id: tradeDoc.id, ...tradeDoc.data() });
  } catch (error) {
    console.error('[Trade] Error loading trade:', error);
    if (error.code === 'permission-denied') {
      throw new Error('Missing or insufficient permissions to access this trade');
    }
    throw error;
  }
}

/**
 * Register user B when they first open the trade
 */
export async function registerUserB(tradeId) {
  const userToken = await getUserToken();
  const tradeRef = doc(db, 'trades', tradeId);
  const tradeDoc = await getDoc(tradeRef);

  if (!tradeDoc.exists()) {
    throw new Error('Trade not found');
  }

  const tradeData = tradeDoc.data();

  if (!tradeData.sideB.userToken) {
    const allowedTokens = tradeData.allowedTokens || [];
    if (!allowedTokens.includes(userToken)) {
      allowedTokens.push(userToken);
    }

    await updateDoc(tradeRef, {
      'sideB.userToken': userToken,
      allowedTokens: allowedTokens,
      updatedAt: serverTimestamp(),
    });
  }
}

/**
 * Update binder info (name, owner) for a side if it's missing.
 * Backfills data for trades created before binder info was stored.
 */
export async function updateBinderInfo(tradeId, side, binderInfo) {
  if (!binderInfo || !binderInfo.name) return;

  const tradeRef = doc(db, 'trades', tradeId);
  const tradeDoc = await getDoc(tradeRef);

  if (!tradeDoc.exists()) return;

  const tradeData = tradeDoc.data();
  const sideData = side === 'A' ? tradeData.sideA : tradeData.sideB;

  if (!sideData?.binderName) {
    const updateField = side === 'A' ? 'sideA' : 'sideB';
    console.log(`[Trade] Backfilling binder info for side ${side}:`, binderInfo.name);
    await updateDoc(tradeRef, {
      [`${updateField}.binderName`]: binderInfo.name,
      [`${updateField}.ownerName`]: binderInfo.owner || null,
      updatedAt: serverTimestamp(),
    });
  }
}

/**
 * Add current user token to allowed tokens list (for iOS recovery after auth loss)
 */
export async function addTokenToAllowedList(tradeId) {
  const userToken = await getUserToken();
  const tradeRef = doc(db, 'trades', tradeId);
  const tradeDoc = await getDoc(tradeRef);

  if (!tradeDoc.exists()) {
    throw new Error('Trade not found');
  }

  const tradeData = tradeDoc.data();
  const allowedTokens = tradeData.allowedTokens || [];

  if (!allowedTokens.includes(userToken)) {
    console.log('[Trade] Adding new auth token to allowed list (iOS recovery)');
    allowedTokens.push(userToken);
    await updateDoc(tradeRef, {
      allowedTokens: allowedTokens,
      updatedAt: serverTimestamp(),
    });
  }
}

/**
 * iOS auth recovery: update a trade side's userToken to the current user's new token.
 * Called when a stored session token matches a side but the current auth token doesn't.
 */
export async function recoverSideToken(tradeId, side) {
  const userToken = await getUserToken();
  const tradeRef = doc(db, 'trades', tradeId);
  const sideKey = `side${side}`;
  await updateDoc(tradeRef, {
    [`${sideKey}.userToken`]: userToken,
    updatedAt: serverTimestamp(),
  });
  console.log('[Trade] Updated side', side, 'token for iOS recovery');
}

// ============================================================
// Trade state helpers
// ============================================================

function isTradeFinalized(trade) {
  return trade.acceptedByA && trade.acceptedByB;
}

export function isTradeComplete(trade) {
  return trade.status === 'complete';
}

function areAllCardsExchanged(cards) {
  if (!cards || cards.length === 0) return true;
  const tradeCards = cards.filter((c) => (c.status || CARD_STATUS.TRADE) === CARD_STATUS.TRADE);
  if (tradeCards.length === 0) return true;
  return tradeCards.every((c) => {
    const exchangedQty = c.exchangedQuantity || 0;
    const totalQty = c.quantity || 1;
    return exchangedQty >= totalQty;
  });
}

function shouldTradeBeComplete(trade) {
  if (!isTradeFinalized(trade)) return false;
  if (trade.status === 'cancelled' || trade.status === 'complete') return false;
  return areAllCardsExchanged(trade.sideA.cards) && areAllCardsExchanged(trade.sideB.cards);
}

// ============================================================
// Card matching helper
// ============================================================

function findCardIndex(cards, cardIdentifier) {
  return cards.findIndex(
    (c) =>
      c.scryfallId === cardIdentifier.scryfallId &&
      c.set === cardIdentifier.set &&
      c.collectorNumber.toLowerCase() === cardIdentifier.collectorNumber.toLowerCase() &&
      c.isFoil === cardIdentifier.isFoil
  );
}

// ============================================================
// Card operations
// ============================================================

/**
 * Update card quantity in trade. Adds card if not present, removes if quantity <= 0.
 * Resets both acceptances when cards change.
 */
export async function updateCardQuantityInTrade(tradeId, side, cardIdentifier, newQuantity) {
  markPendingLocalChange();

  const tradeRef = doc(db, 'trades', tradeId);
  const tradeDoc = await getDoc(tradeRef);

  if (!tradeDoc.exists()) {
    throw new Error('Trade not found');
  }

  const trade = tradeDoc.data();

  if (isTradeFinalized(trade)) {
    console.warn('[Trade] Cannot modify finalized trade');
    return;
  }

  const sideKey = `side${side}`;
  const cards = [...trade[sideKey].cards];
  const cardIndex = findCardIndex(cards, cardIdentifier);

  if (cardIndex !== -1) {
    if (newQuantity <= 0) {
      cards.splice(cardIndex, 1);
    } else {
      cards[cardIndex].quantity = newQuantity;
      cards[cardIndex].priceUsd = cardIdentifier.priceUsd || 0;
      cards[cardIndex].priceEur = cardIdentifier.priceEur || 0;
    }
  } else if (newQuantity > 0) {
    cards.push({
      scryfallId: cardIdentifier.scryfallId,
      name: cardIdentifier.name,
      set: cardIdentifier.set,
      setName: cardIdentifier.setName || cardIdentifier.set?.toUpperCase(),
      setSymbolUrl: cardIdentifier.setSymbolUrl || null,
      collectorNumber: cardIdentifier.collectorNumber,
      isFoil: cardIdentifier.isFoil,
      lang: cardIdentifier.lang || 'en',
      quantity: newQuantity,
      availableQuantity: cardIdentifier.availableQuantity || newQuantity,
      priceUsd: cardIdentifier.priceUsd || 0,
      priceEur: cardIdentifier.priceEur || 0,
      rarity: cardIdentifier.rarity || 'common',
      status: cardIdentifier.status || CARD_STATUS.TRADE,
      addedBy: cardIdentifier.addedBy || null,
    });
  }

  if (cardIndex !== -1 || newQuantity > 0) {
    await updateDoc(tradeRef, {
      [`${sideKey}.cards`]: cards,
      acceptedByA: false,
      acceptedByB: false,
      updatedAt: serverTimestamp(),
    });
    console.log('[Trade] Cards modified, reset both acceptances');
  }
}

/**
 * Subscribe to trade updates via Firestore onSnapshot
 * @returns {Function} Unsubscribe function
 */
export function subscribeToTrade(tradeId, callback) {
  const tradeRef = doc(db, 'trades', tradeId);
  return onSnapshot(tradeRef, (snapshot) => {
    if (snapshot.exists()) {
      callback({ id: snapshot.id, ...snapshot.data() });
    } else {
      callback(null);
    }
  });
}

/**
 * Determine which side of the trade the current user owns
 * @returns {Promise<'A'|'B'|null>}
 */
export async function getUserSide(trade) {
  const userToken = await getUserToken();
  if (trade.sideA.userToken === userToken) return 'A';
  if (trade.sideB.userToken === userToken) return 'B';
  return null;
}

/**
 * Accept the trade for the current user's side
 */
export async function acceptTrade(tradeId, userSide) {
  markPendingLocalChange();

  const tradeRef = doc(db, 'trades', tradeId);
  const tradeDoc = await getDoc(tradeRef);

  if (!tradeDoc.exists()) {
    throw new Error('Trade not found');
  }

  const trade = tradeDoc.data();

  if (!userSide) {
    throw new Error('User side not provided');
  }

  if (trade.acceptedByA && trade.acceptedByB) {
    console.log('[Trade] Trade already finalized');
    return;
  }

  const acceptanceKey = `acceptedBy${userSide}`;

  await updateDoc(tradeRef, {
    [acceptanceKey]: true,
    updatedAt: serverTimestamp(),
  });

  console.log('[Trade] User', userSide, 'accepted the trade');
}

// ============================================================
// Permission check
// ============================================================

function checkStatusChangePermission(userToken, trade, side, card, fromStatus, toStatus) {
  const sideKey = `side${side}`;
  const oppositeSide = side === 'A' ? 'B' : 'A';

  const selectorToken = trade[sideKey].userToken;
  const binderOwnerToken = trade[`side${oppositeSide}`].userToken;

  const isSelector = userToken === selectorToken;
  const isBinderOwner = userToken === binderOwnerToken;
  const isParticipant = isSelector || isBinderOwner;

  if (isParticipant) {
    // Allow transitions between trade and considering
    if (
      (fromStatus === CARD_STATUS.TRADE || fromStatus === CARD_STATUS.CONSIDERING) &&
      (toStatus === CARD_STATUS.TRADE || toStatus === CARD_STATUS.CONSIDERING)
    ) {
      return { allowed: true };
    }
    // Allow accepting suggestions
    if (
      fromStatus === CARD_STATUS.SUGGESTED &&
      (toStatus === CARD_STATUS.TRADE || toStatus === CARD_STATUS.CONSIDERING)
    ) {
      return { allowed: true };
    }
    // Allow removal
    if (toStatus === 'remove') {
      return { allowed: true };
    }
  }

  return { allowed: false, reason: 'Permission denied' };
}

/**
 * Update card status in trade (trade/considering/suggested)
 * @returns {Promise<boolean>} Whether the update succeeded
 */
export async function updateCardStatus(tradeId, side, cardIdentifier, newStatus) {
  markPendingLocalChange();

  const tradeRef = doc(db, 'trades', tradeId);
  const tradeDoc = await getDoc(tradeRef);

  if (!tradeDoc.exists()) {
    throw new Error('Trade not found');
  }

  const trade = tradeDoc.data();

  if (isTradeFinalized(trade)) {
    console.warn('[Trade] Cannot modify finalized trade');
    return false;
  }

  const sideKey = `side${side}`;
  const userToken = await getUserToken();

  const cards = [...trade[sideKey].cards];
  const cardIndex = findCardIndex(cards, cardIdentifier);

  if (cardIndex === -1) {
    console.warn('[Trade] Card not found for status update');
    return false;
  }

  const card = cards[cardIndex];
  const currentStatus = card.status || CARD_STATUS.TRADE;

  const canChangeStatus = checkStatusChangePermission(
    userToken,
    trade,
    side,
    card,
    currentStatus,
    newStatus
  );

  if (!canChangeStatus.allowed) {
    console.warn('[Trade] Status change not allowed:', canChangeStatus.reason);
    return false;
  }

  cards[cardIndex].status = newStatus;

  await updateDoc(tradeRef, {
    [`${sideKey}.cards`]: cards,
    acceptedByA: false,
    acceptedByB: false,
    updatedAt: serverTimestamp(),
  });

  console.log('[Trade] Card status changed from', currentStatus, 'to', newStatus);
  return true;
}

/**
 * Remove card from trade (with permission check)
 */
export async function removeCardFromTrade(tradeId, side, cardIdentifier) {
  markPendingLocalChange();

  const tradeRef = doc(db, 'trades', tradeId);
  const tradeDoc = await getDoc(tradeRef);

  if (!tradeDoc.exists()) {
    throw new Error('Trade not found');
  }

  const trade = tradeDoc.data();

  if (isTradeFinalized(trade)) {
    console.warn('[Trade] Cannot modify finalized trade');
    return { success: false, reason: 'Trade is finalized' };
  }

  const sideKey = `side${side}`;
  const userToken = await getUserToken();

  const cards = [...trade[sideKey].cards];
  const cardIndex = findCardIndex(cards, cardIdentifier);

  if (cardIndex === -1) {
    console.warn('[Trade] Card not found for removal');
    return false;
  }

  const card = cards[cardIndex];
  const currentStatus = card.status || CARD_STATUS.TRADE;

  const canRemove = checkStatusChangePermission(
    userToken,
    trade,
    side,
    card,
    currentStatus,
    'remove'
  );

  if (!canRemove.allowed) {
    console.warn('[Trade] Removal not allowed:', canRemove.reason);
    return { success: false, reason: canRemove.reason };
  }

  cards.splice(cardIndex, 1);

  await updateDoc(tradeRef, {
    [`${sideKey}.cards`]: cards,
    acceptedByA: false,
    acceptedByB: false,
    updatedAt: serverTimestamp(),
  });

  console.log('[Trade] Card removed from trade');
  return { success: true };
}

/**
 * Add a suggested card (binder owner suggesting their own card)
 */
export async function suggestCard(tradeId, side, cardIdentifier) {
  const tradeRef = doc(db, 'trades', tradeId);
  const tradeDoc = await getDoc(tradeRef);

  if (!tradeDoc.exists()) {
    throw new Error('Trade not found');
  }

  const trade = tradeDoc.data();

  if (isTradeFinalized(trade)) {
    console.warn('[Trade] Cannot modify finalized trade');
    return { success: false, reason: 'Trade is finalized' };
  }

  const sideKey = `side${side}`;
  const userToken = await getUserToken();

  // The binder owner of sideX is sideX.userToken (sideA owns binder A, sideB owns binder B)
  const binderOwnerToken = trade[sideKey].userToken;

  if (userToken !== binderOwnerToken) {
    console.warn('[Trade] Only the binder owner can suggest cards');
    return { success: false, reason: 'Only the binder owner can suggest cards' };
  }

  const cards = [...trade[sideKey].cards];

  const existingIndex = findCardIndex(cards, cardIdentifier);

  if (existingIndex !== -1) {
    console.warn('[Trade] Card already in trade');
    return { success: false, reason: 'Card already in trade' };
  }

  cards.push({
    scryfallId: cardIdentifier.scryfallId,
    name: cardIdentifier.name,
    set: cardIdentifier.set,
    setName: cardIdentifier.setName || cardIdentifier.set?.toUpperCase(),
    setSymbolUrl: cardIdentifier.setSymbolUrl || null,
    collectorNumber: cardIdentifier.collectorNumber,
    isFoil: cardIdentifier.isFoil,
    lang: cardIdentifier.lang || 'en',
    quantity: cardIdentifier.quantity || 1,
    availableQuantity: cardIdentifier.availableQuantity || 1,
    priceUsd: cardIdentifier.priceUsd || 0,
    priceEur: cardIdentifier.priceEur || 0,
    rarity: cardIdentifier.rarity || 'common',
    status: CARD_STATUS.SUGGESTED,
    addedBy: userToken,
  });

  await updateDoc(tradeRef, {
    [`${sideKey}.cards`]: cards,
    acceptedByA: false,
    acceptedByB: false,
    updatedAt: serverTimestamp(),
  });

  console.log('[Trade] Card suggested');
  return { success: true };
}

/**
 * Get cards grouped by status for a trade side
 */
export function getCardsByStatus(cards) {
  const trade = [];
  const considering = [];
  const suggested = [];

  for (const card of cards) {
    const status = card.status || CARD_STATUS.TRADE;
    if (status === CARD_STATUS.TRADE) {
      trade.push(card);
    } else if (status === CARD_STATUS.CONSIDERING) {
      considering.push(card);
    } else if (status === CARD_STATUS.SUGGESTED) {
      suggested.push(card);
    }
  }

  return { trade, considering, suggested };
}

// ============================================================
// Exchange tracking
// ============================================================

/**
 * Mark a card as exchanged (received by the other party)
 */
export async function markCardExchanged(tradeId, side, cardIdentifier, exchangedQuantity) {
  markPendingLocalChange();
  markPendingExchangeUpdate();

  const tradeRef = doc(db, 'trades', tradeId);
  const tradeDoc = await getDoc(tradeRef);

  if (!tradeDoc.exists()) {
    throw new Error('Trade not found');
  }

  const trade = tradeDoc.data();

  if (!isTradeFinalized(trade)) {
    console.warn('[Trade] Can only mark cards as exchanged on finalized trades');
    return { success: false, reason: 'Trade is not finalized' };
  }

  if (trade.status === 'complete') {
    console.warn('[Trade] Cannot modify complete trade');
    return { success: false, reason: 'Trade is complete' };
  }

  const sideKey = `side${side}`;
  const cards = [...trade[sideKey].cards];

  const cardIndex = findCardIndex(cards, cardIdentifier);

  if (cardIndex === -1) {
    console.warn('[Trade] Card not found for exchange marking');
    return { success: false, reason: 'Card not found' };
  }

  const maxQuantity = cards[cardIndex].quantity || 1;
  const newExchangedQty = Math.max(0, Math.min(exchangedQuantity, maxQuantity));

  cards[cardIndex].exchangedQuantity = newExchangedQty;
  cards[cardIndex].exchangedAt = newExchangedQty > 0 ? new Date().toISOString() : null;

  /** @type {Record<string, unknown>} */
  const updateData = {
    [`${sideKey}.cards`]: cards,
    updatedAt: serverTimestamp(),
  };

  const updatedTrade = {
    ...trade,
    [sideKey]: { ...trade[sideKey], cards },
  };

  if (shouldTradeBeComplete(updatedTrade)) {
    updateData.status = 'complete';
    console.log('[Trade] All cards exchanged - marking trade as complete');
  }

  await updateDoc(tradeRef, updateData);

  console.log('[Trade] Card marked as', newExchangedQty > 0 ? 'exchanged' : 'not exchanged');
  return { success: true, tradeComplete: updateData.status === 'complete' };
}

/**
 * Mark all cards on a side as exchanged (full quantity)
 */
export async function markAllCardsExchanged(tradeId, side) {
  markPendingLocalChange();

  const tradeRef = doc(db, 'trades', tradeId);
  const tradeDoc = await getDoc(tradeRef);

  if (!tradeDoc.exists()) {
    throw new Error('Trade not found');
  }

  const trade = tradeDoc.data();

  if (!isTradeFinalized(trade)) {
    console.warn('[Trade] Can only mark cards as exchanged on finalized trades');
    return { success: false, reason: 'Trade is not finalized' };
  }

  if (trade.status === 'complete') {
    console.warn('[Trade] Cannot modify complete trade');
    return { success: false, reason: 'Trade is complete' };
  }

  const sideKey = `side${side}`;
  const cards = [...trade[sideKey].cards];
  const now = new Date().toISOString();

  cards.forEach((card) => {
    if ((card.status || CARD_STATUS.TRADE) === CARD_STATUS.TRADE) {
      card.exchangedQuantity = card.quantity || 1;
      card.exchangedAt = now;
    }
  });

  /** @type {Record<string, unknown>} */
  const updateData = {
    [`${sideKey}.cards`]: cards,
    updatedAt: serverTimestamp(),
  };

  const updatedTrade = {
    ...trade,
    [sideKey]: { ...trade[sideKey], cards },
  };

  if (shouldTradeBeComplete(updatedTrade)) {
    updateData.status = 'complete';
    console.log('[Trade] All cards exchanged - marking trade as complete');
  }

  await updateDoc(tradeRef, updateData);

  console.log('[Trade] All cards on side', side, 'marked as exchanged');
  return { success: true, tradeComplete: updateData.status === 'complete' };
}

/**
 * Unmark all cards on a side as not exchanged (reset exchangedQuantity to 0)
 */
export async function unmarkAllCardsExchanged(tradeId, side) {
  markPendingLocalChange();

  const tradeRef = doc(db, 'trades', tradeId);
  const tradeDoc = await getDoc(tradeRef);

  if (!tradeDoc.exists()) {
    throw new Error('Trade not found');
  }

  const trade = tradeDoc.data();

  if (!isTradeFinalized(trade)) {
    console.warn('[Trade] Can only modify exchange status on finalized trades');
    return { success: false, reason: 'Trade is not finalized' };
  }

  if (trade.status === 'complete') {
    console.warn('[Trade] Cannot modify complete trade');
    return { success: false, reason: 'Trade is complete' };
  }

  const sideKey = `side${side}`;
  const cards = [...trade[sideKey].cards];

  cards.forEach((card) => {
    if ((card.status || CARD_STATUS.TRADE) === CARD_STATUS.TRADE) {
      card.exchangedQuantity = 0;
      card.exchangedAt = null;
    }
  });

  await updateDoc(tradeRef, {
    [`${sideKey}.cards`]: cards,
    updatedAt: serverTimestamp(),
  });

  console.log('[Trade] All cards on side', side, 'unmarked as exchanged');
  return { success: true };
}

/**
 * Get exchange progress for a trade side
 */
export function getExchangeProgress(cards) {
  if (!cards || cards.length === 0) return { exchanged: 0, total: 0, complete: true };

  const tradeCards = cards.filter((c) => (c.status || CARD_STATUS.TRADE) === CARD_STATUS.TRADE);

  let totalQuantity = 0;
  let exchangedQuantity = 0;

  for (const card of tradeCards) {
    const qty = card.quantity || 1;
    const exchangedQty = card.exchangedQuantity || 0;
    totalQuantity += qty;
    exchangedQuantity += Math.min(exchangedQty, qty);
  }

  return {
    exchanged: exchangedQuantity,
    total: totalQuantity,
    complete: exchangedQuantity >= totalQuantity,
  };
}

/**
 * Check if the current user has marked all their received cards as exchanged
 */
export async function hasUserCompletedExchange(trade) {
  const userToken = await getUserToken();
  const userSide =
    trade.sideA.userToken === userToken ? 'A' : trade.sideB.userToken === userToken ? 'B' : null;

  if (!userSide) return false;

  const oppositeSide = userSide === 'A' ? 'B' : 'A';
  const oppositeCards = trade[`side${oppositeSide}`].cards;

  const progress = getExchangeProgress(oppositeCards);
  return progress.complete;
}
